import { MessageOutlined, SearchOutlined, WalletOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Button, Input, message, Segmented, Select, Spin, Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useMemo, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import type { AppRole } from "@/shared/config/navigation";
import { PaymentsNavigation } from "../components/PaymentsNavigation";
import { getPaymentSchedules } from "../services/payment-schedules.api";
import type { PaymentScheduleRow, PaymentStatusFilter } from "../types/payments.types";
import { formatUzs } from "../utils/payments.utils";

const statusText: Record<PaymentScheduleRow["status"], string> = {
	paid: "To'landi",
	overdue: "Kechikkan",
	upcoming: "Yaqin",
	planned: "Rejada",
	partial: "Qisman",
};

const statusColor: Record<PaymentScheduleRow["status"], string> = {
	paid: "green",
	overdue: "red",
	upcoming: "gold",
	planned: "default",
	partial: "blue",
};

export function PaymentSchedulesPage() {
	const navigate = useNavigate();
	const { role } = useOutletContext<{ role: AppRole }>();

	const schedulesQuery = useQuery({
		queryKey: ["payment-schedules", "all-for-page"],
		queryFn: () => getPaymentSchedules({ limit: 100, page: 1 }),
	});
	const schedules = schedulesQuery.data?.data ?? [];

	const rows = useMemo(() => {
		return schedules.map((backendItem) => {
			const amountUzs = backendItem.amount;
			const paidAmountUzs = backendItem.paidAmount;
			const remainingUzs = Math.max(0, amountUzs - paidAmountUzs);

			return {
				id: backendItem.id,
				saleId: backendItem.saleId,
				saleNumber: backendItem.saleNumber
					? String(backendItem.saleNumber)
					: backendItem.saleId.substring(0, 6).toUpperCase(),
				scheduleIndex: 1,
				clientName: backendItem.client?.fullName ?? "Mijoz yo'q (Backend)",
				phone: backendItem.client?.phone ?? "-",
				roomNumber: backendItem.roomNumber ?? "Xona (Backend)",
				buildingName: backendItem.buildingName ?? "Bino yo'q (Backend)",
				dueDate: backendItem.dueDate ? backendItem.dueDate.split("T")[0] : "",
				amountUzs,
				paidAmountUzs,
				remainingUzs: Math.round(remainingUzs * 100) / 100,
				delayDays: backendItem.daysOverdue ?? 0,
				status: backendItem.status as PaymentScheduleRow["status"],
				managerName: "Menejer yo'q (Backend)",
			};
		});
	}, [schedules]);

	const [statusFilter, setStatusFilter] = useState<PaymentStatusFilter>("overdue");
	const [building, setBuilding] = useState("all");
	const [searchQuery, setSearchQuery] = useState("");

	const buildingOptions = useMemo(
		() => ["all", ...Array.from(new Set(rows.map((row) => row.buildingName)))],
		[rows]
	);

	const filteredRows = useMemo(() => {
		const query = searchQuery.toLowerCase();

		return rows.filter((row) => {
			const matchesStatus =
				statusFilter === "all" ||
				row.status === statusFilter ||
				(statusFilter === "upcoming" && row.status === "planned");
			const matchesBuilding = building === "all" || row.buildingName === building;
			const matchesSearch =
				row.clientName.toLowerCase().includes(query) ||
				row.roomNumber.includes(query) ||
				row.saleNumber.includes(query);

			return matchesStatus && matchesBuilding && matchesSearch;
		});
	}, [building, rows, searchQuery, statusFilter]);

	const overdueTotal = rows
		.filter((row) => row.status === "overdue")
		.reduce((sum, row) => sum + row.remainingUzs, 0);
	const upcomingTotal = rows
		.filter((row) => row.status === "upcoming" || row.status === "planned")
		.reduce((sum, row) => sum + row.remainingUzs, 0);

	const columns: ColumnsType<PaymentScheduleRow> = [
		{
			title: "Mijoz",
			key: "client",
			render: (_, record) => (
				<div className="crm-org-name-cell">
					<span className="crm-mini-avatar amber">
						{record.clientName
							.split(" ")
							.map((part) => part[0])
							.join("")
							.slice(0, 2)}
					</span>
					<div>
						<strong>{record.clientName}</strong>
						<span>{record.phone}</span>
					</div>
				</div>
			),
		},
		{
			title: "Xona",
			key: "room",
			render: (_, record) => (
				<div>
					<strong>{record.roomNumber}</strong>
					<span className="crm-table-sub">{record.buildingName}</span>
				</div>
			),
		},
		{ title: "Sana", dataIndex: "dueDate", key: "dueDate", width: 120 },
		{
			title: "Summa",
			dataIndex: "remainingUzs",
			key: "remainingUzs",
			align: "right",
			render: (value: number, record) => (
				<div className="crm-money-cell">
					<strong>{formatUzs(value)}</strong>
					<span>reja {formatUzs(record.amountUzs)}</span>
				</div>
			),
		},
		{
			title: "Holat",
			dataIndex: "status",
			key: "status",
			render: (value: PaymentScheduleRow["status"]) => (
				<Tag color={statusColor[value]}>{statusText[value]}</Tag>
			),
		},
		{
			title: "Kechikish",
			dataIndex: "delayDays",
			key: "delayDays",
			render: (value: number, record) =>
				record.status === "overdue" ? (
					<span className="crm-payment-danger">{value} kun</span>
				) : (
					<span className="crm-muted-text">-</span>
				),
		},
		{
			title: "",
			key: "actions",
			width: 150,
			render: (_, record) => (
				<Button
					size="small"
					icon={<MessageOutlined />}
					onClick={(event) => {
						event.stopPropagation();
						message.info(`${record.clientName} uchun SMS eslatma tayyorlandi`);
					}}
				>
					SMS
				</Button>
			),
		},
	];

	return (
		<div className="crm-payment-schedules">
			<div className="crm-page-head">
				<div>
					<h1>To'lov grafiklari</h1>
					<p>Kechikkan va muddati yaqin to'lovlar nazorati</p>
				</div>
				<Button
					className="crm-primary-btn"
					type="primary"
					icon={<WalletOutlined />}
					onClick={() => navigate("/payments/new")}
				>
					To'lov kiritish
				</Button>
			</div>

			<PaymentsNavigation activeTab="overdue" role={role} />

			<div className="crm-payment-schedule-summary">
				<div className="crm-kpi">
					<div className="crm-kpi-title">
						<span className="crm-kpi-icon amber">
							<WalletOutlined />
						</span>
						Jami kechikkan
					</div>
					<div className="crm-kpi-value">
						<strong>{formatUzs(overdueTotal)}</strong>
					</div>
				</div>
				<div className="crm-kpi">
					<div className="crm-kpi-title">
						<span className="crm-kpi-icon sky">
							<WalletOutlined />
						</span>
						Yaqin va rejadagi
					</div>
					<div className="crm-kpi-value">
						<strong>{formatUzs(upcomingTotal)}</strong>
					</div>
				</div>
			</div>

			<div className="crm-card crm-payment-toolbar">
				<Segmented
					value={statusFilter}
					onChange={(value) => setStatusFilter(value as PaymentStatusFilter)}
					options={[
						{ label: "Kechikkan", value: "overdue" },
						{ label: "Yaqin", value: "upcoming" },
						{ label: "Hammasi", value: "all" },
					]}
				/>
				<Select
					value={building}
					onChange={setBuilding}
					options={buildingOptions.map((item) => ({
						label: item === "all" ? "Bino: barchasi" : item,
						value: item,
					}))}
					style={{ width: 180 }}
				/>
				<Input
					placeholder="Mijoz, xona, shartnoma..."
					prefix={<SearchOutlined />}
					value={searchQuery}
					onChange={(event) => setSearchQuery(event.target.value)}
					style={{ maxWidth: 280 }}
				/>
			</div>

			<div className="crm-card crm-table-card">
				{schedulesQuery.isLoading ? (
					<div style={{ textAlign: "center", padding: "32px 0" }}>
						<Spin />
					</div>
				) : (
					<Table
						columns={columns}
						dataSource={filteredRows}
						rowKey="id"
						scroll={{ x: 920 }}
						pagination={{ pageSize: 10 }}
					/>
				)}
			</div>
		</div>
	);
}

export default PaymentSchedulesPage;
